import { MAX_RETAINED_EVENTS } from '@/hooks/use-agent-run-events';
import type { AgentChatActivityEvent } from '@/services/agents/chat-stream';

/**
 * One step of an assistant turn, in the order it streamed in. Thinking
 * deltas merge into the open thinking segment; text is the prose the model
 * wrote before moving on to another step (the reply in progress is the
 * message's `content`, not a segment, until something seals it).
 */
export type TurnSegment =
  | { kind: 'thinking'; text: string; startedAt: number; endedAt?: number }
  | { kind: 'text'; text: string }
  | { kind: 'activity'; event: AgentChatActivityEvent };

// A bound so a runaway tool loop can't grow the array -- which is copied on
// every append -- without end. The newest segments are the ones worth keeping.
const MAX_SEGMENTS = MAX_RETAINED_EVENTS;

function capped(segments: TurnSegment[]): TurnSegment[] {
  return segments.length > MAX_SEGMENTS
    ? segments.slice(-MAX_SEGMENTS)
    : segments;
}

/** Marks the open thinking segment, if any, as finished at `at`. */
export function closeThinking(
  segments: TurnSegment[],
  at: number,
): TurnSegment[] {
  const last = segments[segments.length - 1];
  if (last?.kind !== 'thinking' || last.endedAt !== undefined) return segments;
  return [...segments.slice(0, -1), { ...last, endedAt: at }];
}

/**
 * Moves the reply in progress into the timeline as an intermediate message.
 * Returns the unchanged timeline when there is no text to move.
 */
export function sealText(
  segments: TurnSegment[],
  content: string,
): TurnSegment[] {
  const text = content.trim();
  if (!text) return segments;
  return capped([...segments, { kind: 'text', text }]);
}

export function appendThinking(
  segments: TurnSegment[],
  delta: string,
  at: number,
): TurnSegment[] {
  const last = segments[segments.length - 1];
  if (last?.kind === 'thinking' && last.endedAt === undefined) {
    return [...segments.slice(0, -1), { ...last, text: last.text + delta }];
  }
  return capped([
    ...segments,
    { kind: 'thinking', text: delta, startedAt: at },
  ]);
}

export function appendActivity(
  segments: TurnSegment[],
  event: AgentChatActivityEvent,
  at: number,
): TurnSegment[] {
  return capped([...closeThinking(segments, at), { kind: 'activity', event }]);
}

/**
 * Whether a frame ends the prose before it. The model only writes a new
 * paragraph of reply after it thinks again or calls a tool; a status frame
 * is lifecycle noise that can land mid-reply, and splitting the answer on
 * it would cut one paragraph of the reply in two.
 */
export function endsProse(
  frame: 'thinking' | AgentChatActivityEvent['type'],
): boolean {
  return frame === 'thinking' || frame === 'tool_call';
}

/**
 * Closes the timeline at turn end. The final reply is shown as the message
 * itself, so the last text segment is dropped when it repeats it -- a
 * thinking or tool frame after the reply seals it into the timeline, so it
 * need not be the last segment.
 */
export function finishTimeline(
  segments: TurnSegment[],
  finalReply: string,
  at: number,
): TurnSegment[] {
  const closed = closeThinking(segments, at);
  const reply = finalReply.trim();
  for (let i = closed.length - 1; i >= 0; i--) {
    const s = closed[i];
    if (s.kind !== 'text') continue;
    return s.text === reply
      ? [...closed.slice(0, i), ...closed.slice(i + 1)]
      : closed;
  }
  return closed;
}

export function activityEvents(
  segments: TurnSegment[],
): AgentChatActivityEvent[] {
  return segments.flatMap((s) => (s.kind === 'activity' ? [s.event] : []));
}

export function hasProse(segments: TurnSegment[] | undefined): boolean {
  return !!segments?.some((s) => s.kind === 'text');
}

// The orchestrator sends this status right before each thinking stream.
const THINKING_STATUS = /^thinking(…|\.\.\.)?$/i;

/**
 * The status that only announces thinking. It never enters the timeline:
 * the thinking block says as much, and since a status does not end the
 * prose, keeping it would put it ahead of prose still streaming and leave
 * it as a step group of its own. Any other status is kept.
 */
export function isThinkingMarker(event: AgentChatActivityEvent): boolean {
  return (
    event.type === 'status' && THINKING_STATUS.test((event.text ?? '').trim())
  );
}

/**
 * A turn as it reads in the chat: prose inline, and each run of thinking and
 * steps between two stretches of prose as one collapsible group.
 */
export type TurnBlock =
  | { kind: 'steps'; segments: TurnSegment[] }
  | { kind: 'text'; text: string };

export function groupTimeline(segments: TurnSegment[]): TurnBlock[] {
  const blocks: TurnBlock[] = [];
  for (const segment of segments) {
    if (segment.kind === 'text') {
      blocks.push({ kind: 'text', text: segment.text });
      continue;
    }
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'steps') {
      last.segments.push(segment);
    } else {
      blocks.push({ kind: 'steps', segments: [segment] });
    }
  }
  return blocks;
}

export function segmentsStartedAt(segments: TurnSegment[]): number | undefined {
  for (const s of segments) {
    if (s.kind === 'thinking' && s.startedAt) return s.startedAt;
    if (s.kind === 'activity' && s.event.at) return s.event.at;
  }
  return undefined;
}
